import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
// MIGRATION: fully on AWS PostgREST.
import { aws } from './awsdb';
import { authWrite } from './authWrite';
import { contactDisplayName, contactSubtitle, contactMatchesQuery } from './contacts';

function autoCap(s) {
  return s.replace(/\b\w/g, c => c.toUpperCase());
}

export default function ContactAutocomplete({ session, value, onChange, placeholder = 'Search or type name...', autoFocus, className = '' }) {
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [showCreate, setShowCreate] = useState(false);
  const wrapRef = useRef(null);
  const listRef = useRef(null);

  const load = useCallback(async () => {
    // Order by contact_person first (person-centric view), fall back to
    // company_name for company-only contacts.
    const { data } = await aws.from('contacts')
      .select('id, company_name, contact_person, category')
      .is('archived_at', null)
      .order('contact_person', { nullsFirst: false })
      .order('company_name');
    setContacts(data || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) { setOpen(false); setActiveIdx(-1); }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = useMemo(() => {
    const q = (value || '').trim().toLowerCase();
    if (!q) return contacts.slice(0, 10);
    return contacts.filter(c => contactMatchesQuery(c, q)).slice(0, 10);
  }, [contacts, value]);

  // Exact match against EITHER the person name or the company name — so we
  // don't show a confusing "+ Create 'Nathan'" when Nathan is already a
  // contact, even if his row is keyed by Phyto-Therapy.
  const exactMatch = contacts.some(c => {
    const v = (value || '').toLowerCase();
    return (c.company_name || '').toLowerCase() === v
        || (c.contact_person || '').toLowerCase() === v;
  });
  const showCreateOption = value && value.trim() && !exactMatch;
  const totalOptions = filtered.length + (showCreateOption ? 1 : 0);

  function handleChange(e) {
    const capped = autoCap(e.target.value);
    onChange(capped);
    setOpen(true);
    setActiveIdx(-1);
  }

  function selectOption(name) {
    onChange(name);
    setOpen(false);
    setActiveIdx(-1);
  }

  function handleKeyDown(e) {
    if (!open) {
      if (e.key === 'ArrowDown') { setOpen(true); e.preventDefault(); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, totalOptions - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      if (activeIdx < filtered.length) {
        const c = filtered[activeIdx];
        selectOption(c.company_name || c.contact_person || '');
      } else if (showCreateOption) {
        setShowCreate(true);
        setOpen(false);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIdx(-1);
    }
  }

  // Scroll active option into view
  useEffect(() => {
    if (activeIdx >= 0 && listRef.current) {
      const el = listRef.current.children[activeIdx];
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIdx]);

  function handleCreateDone(name) {
    if (name) {
      onChange(name);
      load(); // refresh contacts list
    }
    setShowCreate(false);
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        className={`form-input ${className}`}
        value={value}
        onChange={handleChange}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoFocus={autoFocus}
      />
      {open && (value || filtered.length > 0) && (
        <div className="supplier-dropdown" ref={listRef}>
          {filtered.map((c, i) => {
            // Display person-first by default. The selected value passed
            // back to the caller is still company_name (preserves the
            // existing PO/supplier flows that key on company). If a
            // contact has only a person and no company, send the person
            // string back instead so the caller still gets a usable name.
            const primary = contactDisplayName(c, 'person');
            const subtitle = contactSubtitle(c, 'person');
            const selectValue = c.company_name || c.contact_person || '';
            return (
              <button key={c.id} type="button"
                className={`supplier-option ${i === activeIdx ? 'active' : ''}`}
                style={i === activeIdx ? { background: 'var(--bg-hover, #f4f4f5)' } : undefined}
                onClick={() => selectOption(selectValue)}>
                <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0, flex: 1 }}>
                  <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>{primary}</span>
                  {subtitle && (
                    <span style={{ fontSize: 10, color: 'var(--text-subdued)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>{subtitle}</span>
                  )}
                </span>
                {c.category && <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--text-subdued)', paddingLeft: 8 }}>{c.category}</span>}
              </button>
            );
          })}
          {showCreateOption && (
            <button type="button"
              className={`supplier-option supplier-create ${activeIdx === filtered.length ? 'active' : ''}`}
              style={activeIdx === filtered.length ? { background: 'var(--bg-hover, #f4f4f5)' } : undefined}
              onClick={() => { setShowCreate(true); setOpen(false); }}>
              + Create "{autoCap(value.trim())}"
            </button>
          )}
        </div>
      )}
      {showCreate && <CreateContactModal session={session} defaultName={autoCap(value.trim())} onClose={handleCreateDone} />}
    </div>
  );
}

function CreateContactModal({ session, defaultName, onClose }) {
  // Person OR company OR both. At least one required. The autocomplete
  // pre-fills whatever the user typed into the search field as the Person
  // Name (the most common shape — they were searching for a human).
  const [contactPerson, setContactPerson] = useState(defaultName);
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [category, setCategory] = useState('Supplier');
  const [region, setRegion] = useState('AU');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function submit() {
    const personTrim = contactPerson.trim();
    const companyTrim = companyName.trim();
    if (!personTrim && !companyTrim) {
      return setError('Enter a person name, a company name, or both.');
    }
    setSaving(true);
    try {
      const inserted = await authWrite(session, 'contacts', 'insert', {
        // company_name kept first in the row for table-display consistency,
        // but either field can be null now.
        company_name: companyTrim ? autoCap(companyTrim) : null,
        contact_person: personTrim ? autoCap(personTrim) : null,
        email: email.trim() || null,
        category,
        region,
        status: 'active',
        created_by: session?.code || 'inline',
      });
      const created = Array.isArray(inserted) ? inserted[0] : null;
      if (!created) {
        setError('No row returned'); setSaving(false); return;
      }
      // Pass whichever name was set back to the caller (autocomplete
      // upstream will display this as the chosen value).
      onClose(created.company_name || created.contact_person);
    } catch (err) {
      setError(err.message || 'Save failed');
      setSaving(false);
    }
  }

  return createPortal(
    <>
      <div className="quickpay-backdrop" onClick={() => onClose(null)} />
      <div className="quickpay-modal" role="dialog" style={{ maxWidth: 400 }}>
        <div className="quickpay-title"><span>New Contact</span></div>
        <div className="quickpay-row">
          <label>Person Name</label>
          <input className="form-input" value={contactPerson} onChange={e => setContactPerson(autoCap(e.target.value))} placeholder="e.g. Andrew" autoFocus />
        </div>
        <div className="quickpay-row">
          <label>Company Name</label>
          <input className="form-input" value={companyName} onChange={e => setCompanyName(autoCap(e.target.value))} placeholder="e.g. Phyto-Therapy Pty Ltd" />
        </div>
        <div className="quickpay-row" style={{ fontSize: 11, color: 'var(--text-subdued)', padding: '0 16px', marginTop: -8 }}>
          Fill in either, or both. At least one is required.
        </div>
        <div className="quickpay-row">
          <label>Contact Email</label>
          <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@example.com" />
        </div>
        <div className="quickpay-row">
          <label>Category</label>
          <select className="form-select" value={category} onChange={e => setCategory(e.target.value)}>
            <option value="Supplier">Supplier</option>
            <option value="Customer">Customer</option>
            <option value="Both">Both</option>
          </select>
        </div>
        <div className="quickpay-row">
          <label>Region</label>
          <select className="form-select" value={region} onChange={e => setRegion(e.target.value)}>
            <option value="AU">AU</option>
            <option value="US">US</option>
            <option value="EU">EU</option>
            <option value="UK">UK</option>
            <option value="Other">Other</option>
          </select>
        </div>
        {error && <div style={{ color: 'var(--text-error)', fontSize: 12, padding: '0 16px' }}>{error}</div>}
        <div className="quickpay-actions">
          <button className="btn btn-secondary btn-sm" onClick={() => onClose(null)}>Cancel</button>
          <button className="btn btn-primary btn-sm" disabled={saving} onClick={submit}>{saving ? 'Saving...' : 'Create'}</button>
        </div>
      </div>
    </>,
    document.body
  );
}
